import React from 'react';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { 
  MoonIcon, 
  SunIcon
} from '@heroicons/react/24/outline';

interface AdminLayoutProps {
  children: React.ReactNode;
}

const AdminLayout: React.FC<AdminLayoutProps> = ({ children }) => {
  const { user, logout } = useAuth();
  const { isDarkMode, toggleDarkMode } = useTheme();

  return (
    <div className={`min-h-screen transition-colors duration-200 ${isDarkMode ? 'bg-black' : 'bg-gray-50'}`}>
      
      {/* Admin Header */}
      <header 
        className={`
          sticky top-0 z-50 shadow-sm border-b transition-all duration-200 h-16
          ${isDarkMode 
            ? 'bg-gray-900 border-gray-700 shadow-black/10' 
            : 'bg-white border-neutral-200 shadow-neutral-900/5'
          }
        `}
        role="banner"
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-full">
          <div className="flex items-center justify-between h-full">
            {/* Admin Logo */}
            <div className="flex items-center gap-3">
              <div 
                className={`
                  w-8 h-8 sm:w-10 sm:h-10 rounded-lg sm:rounded-xl 
                  flex items-center justify-center
                  bg-gradient-to-br from-purple-500 to-purple-600
                  shadow-md sm:shadow-lg transition-all duration-300 hover:scale-105
                  ${isDarkMode ? 'shadow-purple-900/50' : 'shadow-purple-500/25'}
                `}
                role="img"
                aria-label="Admin panel logo"
              >
                <span className="text-white font-bold text-sm sm:text-base tracking-tight" aria-hidden="true">A</span>
              </div>
              <div className="hidden sm:block">
                <h1 className={`text-lg font-bold tracking-tight transition-colors duration-200 ${
                  isDarkMode ? 'text-white' : 'text-gray-900'
                }`}>
                  Admin Panel
                </h1>
                <p className={`text-xs font-medium transition-colors duration-200 ${
                  isDarkMode ? 'text-gray-400' : 'text-gray-500'
                }`}>
                  Voice Assistant Management
                </p>
              </div>
            </div>

            {/* Right side controls */}
            <div className="flex items-center gap-3 sm:gap-4">
              {/* Dark Mode Toggle */}
              <button
                onClick={toggleDarkMode}
                className={`
                  p-2 rounded-xl transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring
                  ${isDarkMode 
                    ? 'text-gray-300 hover:text-white hover:bg-gray-800' 
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                  }
                `}
                aria-label={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
                aria-pressed={isDarkMode}
              >
                {isDarkMode ? (
                  <SunIcon className="h-5 w-5" aria-hidden="true" />
                ) : (
                  <MoonIcon className="h-5 w-5" aria-hidden="true" />
                )}
              </button>
              
              {/* Admin Email */}
              <span 
                className={`hidden sm:block text-sm max-w-[200px] truncate transition-colors duration-200 ${
                  isDarkMode ? 'text-gray-300' : 'text-gray-600'
                }`}
                aria-label={`Logged in as ${user?.email}`}
              >
                {user?.email}
              </span>
              
              {/* Logout Button */}
              <button
                onClick={logout}
                className={`
                  p-2 rounded-xl transition-colors duration-200 font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring
                  text-sm px-3 py-2 border
                  ${isDarkMode 
                    ? 'text-gray-300 border-gray-600 hover:text-white hover:bg-gray-800 hover:border-gray-500' 
                    : 'text-gray-600 border-gray-300 hover:text-gray-900 hover:bg-gray-100 hover:border-gray-400'
                  }
                `}
                aria-label="Sign out of admin panel"
              >
                <span className="hidden sm:inline">Logout</span>
                <span className="sm:hidden">Exit</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main content */}
      <main 
        className={`
          transition-colors duration-200 
          ${isDarkMode ? 'bg-black' : 'bg-gray-50'}
        `}
        role="main"
        aria-label="Admin panel content"
      >
        {children}
      </main>
    </div>
  );
};

export default AdminLayout;